/**
 * WebRTCSignaling.js
 * Native WebRTC Signaling Client for Birmingham Institute Interactive Videocall.
 * Supports P2P signaling via multi-channel adapters:
 * 1. BroadcastChannel / Storage events (Instant zero-latency sync in same browser / multi-tab / testing)
 * 2. REST API Polling / HTTP Signaling exchange with the Birmingham backend
 */

export class WebRTCSignaling {
    constructor(roomId, userId, role = 'student', options = {}) {
        this.roomId = roomId;
        this.userId = userId;
        this.role = role; // 'student' | 'teacher'
        this.apiDomain = options.apiDomain || 'https://cursos.birmingham-institute.com/';
        this.apiCode = options.apiCode || 'pAiPZ1BNInRc23kq7MtoWsa5FxLXQ9dVzCYSerDfvK6mEwTJgUbG4j0OH8luhy';
        this.callbacks = {
            onOffer: () => {},
            onAnswer: () => {},
            onIceCandidate: () => {},
            onPeerJoined: () => {},
            onPeerLeft: () => {},
            onMessage: () => {},
            onStatusChange: () => {}
        };
        this.isDestroyed = false;
        this.pollInterval = null;
        this.lastProcessedMessageId = 0;

        this.initBroadcastChannel();
    }

    initBroadcastChannel() {
        try {
            if (typeof window !== 'undefined' && 'BroadcastChannel' in window) {
                this.channel = new BroadcastChannel(`bi_webrtc_room_${this.roomId}`);
                this.channel.onmessage = (event) => {
                    this.handleIncomingMessage(event.data);
                };
            }
        } catch (e) {
            console.warn('[WebRTC Signaling] BroadcastChannel unavailable, using storage fallback', e);
        }

        // Storage event fallback for cross-tab signaling
        this.storageListener = (e) => {
            if (e.key === `bi_webrtc_signal_${this.roomId}` && e.newValue) {
                try {
                    const data = JSON.parse(e.newValue);
                    this.handleIncomingMessage(data);
                } catch (err) {
                    // Ignore parse error
                }
            }
        };
        if (typeof window !== 'undefined') {
            window.addEventListener('storage', this.storageListener);
        }
    }

    on(event, callback) {
        if (this.callbacks[event] !== undefined) {
            this.callbacks[event] = callback;
        }
    }

    send(type, payload) {
        if (this.isDestroyed) return;

        const message = {
            roomId: this.roomId,
            senderId: this.userId,
            senderRole: this.role,
            type,
            payload,
            timestamp: Date.now(),
            id: `${this.userId}_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`
        };

        // 1. Send via BroadcastChannel if available
        if (this.channel) {
            try {
                this.channel.postMessage(message);
            } catch (e) {
                console.warn('[WebRTC Signaling] Error posting to channel', e);
            }
        }

        // 2. Send via LocalStorage event for cross-tab sync
        try {
            localStorage.setItem(`bi_webrtc_signal_${this.roomId}`, JSON.stringify(message));
        } catch (e) {}

        // 3. Send to Backend REST API (if server signaling endpoint exists)
        this.sendToServer(message);
    }

    async sendToServer(message) {
        try {
            const url = `${this.apiDomain}api/v1/webrtcSignal/${this.apiCode}`;
            const body = new FormData();
            body.append('room_id', this.roomId);
            body.append('user_id', this.userId);
            body.append('role', this.role);
            body.append('type', message.type);
            body.append('payload', JSON.stringify(message.payload));

            await fetch(url, {
                method: 'POST',
                body
            }).catch(() => {
                // Silently handle if backend endpoint is in progress
            });
        } catch (e) {}
    }

    handleIncomingMessage(message) {
        if (!message || message.roomId !== this.roomId || message.senderId === this.userId) {
            return;
        }

        switch (message.type) {
            case 'JOIN':
                this.callbacks.onPeerJoined(message);
                break;
            case 'OFFER':
                this.callbacks.onOffer(message.payload, message);
                break;
            case 'ANSWER':
                this.callbacks.onAnswer(message.payload, message);
                break;
            case 'ICE_CANDIDATE':
                this.callbacks.onIceCandidate(message.payload, message);
                break;
            case 'CHAT':
            case 'WHITEBOARD':
                this.callbacks.onMessage(message);
                break;
            case 'LEAVE':
                this.callbacks.onPeerLeft(message);
                break;
            default:
                break;
        }
    }

    start() {
        this.isDestroyed = false;
        // Announce presence
        this.send('JOIN', { role: this.role, userId: this.userId });
    }

    destroy() {
        this.isDestroyed = true;
        this.send('LEAVE', { role: this.role, userId: this.userId });

        if (this.channel) {
            try {
                this.channel.close();
            } catch (e) {}
            this.channel = null;
        }

        if (typeof window !== 'undefined' && this.storageListener) {
            window.removeEventListener('storage', this.storageListener);
        }

        if (this.pollInterval) {
            clearInterval(this.pollInterval);
            this.pollInterval = null;
        }
    }
}
